import axios, { CanceledError } from "axios";
import router from "@/router";
import { billingUrl } from "@/utils/billing-url";
import { useToast } from "vue-toastification";
import pinia, { useAuthStore } from "@/state/pinia";
import { useLocaleStore } from '@/state/pinia/locale'
import { useProjectStore } from "@/state/pinia/project";

const toast = useToast();

// Flag to prevent multiple error toasts during logout
let isHandlingAuthFailure = false;

/**
 * Helper function to check if an error should be silently ignored
 * (e.g., authentication errors during logout redirect)
 */
export const isAuthError = (error) => {
    return error instanceof CanceledError && (error.message === "unauthenticated" || error.message === "unauthorized");
};

/**
 * Check if the interceptor already showed a toast for this error.
 * Use in catch blocks to avoid double-toasting:
 *   if (!errorToastShown(error)) toast.error('fallback')
 */
export const errorToastShown = (error) => !!error?._toastShown;

const api = axios.create({
    baseURL: process.env.VUE_APP_BE_URL,
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
});

api.interceptors.request.use((config) => {
    let token = null;
    try {
        const auth = useAuthStore(pinia);
        token = auth.getToken?.();
    } catch {
        token = localStorage.getItem("auth_token");
    }
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
    }

    const store = useLocaleStore()
    const lang = (store && store.get && store.get()) || store.lang || 'pl'
    config.headers = config.headers || {}
    config.headers['Accept-Language'] = lang
    config.headers['X-Lang'] = lang // opcjonalnie

    // Replace project code with UUID in API URLs for unambiguous resolution
    // Matches /api/projects/{code}/... and replaces code with UUID if known
    if (config.url) {
        const match = config.url.match(/\/api\/projects\/([^/]+)/);
        if (match && match[1]) {
            const projectRef = match[1];
            // Skip if already a UUID
            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(projectRef)) {
                try {
                    const ps = useProjectStore(pinia);
                    // Check active project first (always available), then project list
                    const uuid = (ps.activeProjectObj?.code === projectRef && ps.activeProjectObj?.uuid)
                        ? ps.activeProjectObj.uuid
                        : ps.all.find(p => p.code === projectRef)?.uuid;
                    if (uuid) {
                        config.url = config.url.replace(
                            `/api/projects/${projectRef}`,
                            `/api/projects/${uuid}`
                        );
                    }
                } catch {
                    // Store not ready, keep code
                }
            }
        }
    }

    return config;
});

api.interceptors.response.use(
    (response) => {
        // Reset auth failure flag on successful request
        isHandlingAuthFailure = false;
        return response;
    },
    (error) => {
        const { data, status } = error?.response || {};

        if (status === 401 && !error.config?.skipAuthRedirect) {
            // Prevent multiple toasts and redirects during logout
            if (!isHandlingAuthFailure) {
                isHandlingAuthFailure = true;

                // Clear all existing toasts to prevent error messages from showing
                toast.clear();

                const auth = useAuthStore(pinia);
                const projectStore = useProjectStore(pinia);
                // Per-user activeProject key is preserved across logout/login
                projectStore.resetStore();
                auth.logout();

                // Redirect to SSO if enabled, otherwise local login
                if (auth.isSsoEnabled && auth.isSsoEnabled()) {
                    auth.redirectToSso();
                } else if (router.currentRoute.value.path !== "/login") {
                    router.replace("/login").catch(() => {});
                }
            }

            return Promise.reject(new CanceledError("unauthenticated"));
        }

        if (status === 422 && data?.error === "plan_limit_exceeded") {
            toast.info("Kliknij tutaj aby zwiększyć swoje limity!", {
                toastClassName: "upgrade-pointer",
                // Wyższy plan kupuje się w panelu rozliczeń w SSO — tu tylko odsyłamy.
                onClick: () => {
                    const upgradeUrl = billingUrl();
                    if (upgradeUrl) {
                        window.location.href = upgradeUrl;
                    } else {
                        router.push({ name: "Subscriptions" }).catch(() => {});
                    }
                },
            });
            error._toastShown = true;
            return Promise.reject(error);
        }

        // 402 = nieopłacony abonament, panel działa tylko do odczytu.
        // Backend zwraca to na każdej próbie zapisu, więc komunikat musi
        // prowadzić prosto do rozliczeń — inaczej user tylko widzi, że
        // „nie da się zapisać", i nie wie dlaczego.
        if (status === 402) {
            toast.error(data?.message || "Abonament jest nieopłacony — zapisywanie jest wstrzymane.", {
                toastClassName: "upgrade-pointer",
                onClick: () => {
                    const url = billingUrl();
                    if (url) window.location.href = url;
                },
            });
            error._toastShown = true;
            return Promise.reject(error);
        }

        if (status === 428 && data?.code === "workspace_required") {
            const target = data?.redirect || "/workspaces/new";
            if (router.currentRoute.value.path !== target) {
                router.replace(target).catch(() => {});
            }
            return Promise.reject(new CanceledError("workspace_required"));
        }

        // 403 = permission denied — silently reject (not an error, just no access)
        if (status === 403) {
            return Promise.reject(new CanceledError("unauthorized"));
        }

        // Auto-show backend error message in toast (unless request opted out)
        if (!error.config?.skipErrorToast && status && status !== 401) {
            let msg = null;

            // For 422 validation errors, extract the first field error
            if (status === 422 && data?.errors && !data?.error) {
                const firstField = Object.keys(data.errors)[0];
                if (firstField) {
                    msg = data.errors[firstField][0];
                }
            }

            // Fallback to response message
            if (!msg && data?.message) {
                msg = data.message;
            }

            if (msg) {
                toast.error(msg);
                error._toastShown = true;
            }
        }

        return Promise.reject(error);
    }
);

export default api;
